/**
 * OpenTelemetry types for trace context management
 */

/**
 * W3C Trace Flags
 * - 0x00: Not sampled
 * - 0x01: Sampled (trace will be recorded)
 */
export enum TraceFlags {
  NONE = 0x00,
  SAMPLED = 0x01,
}

/**
 * Trace context containing all information needed for distributed tracing
 */
export interface TraceContext {
  /** W3C Trace ID (32 hex characters) */
  traceId: string;

  /** W3C Span ID (16 hex characters) */
  spanId: string;

  /** Trace flags indicating sampling status */
  traceFlags: TraceFlags;

  /** Optional trace state for vendor-specific data */
  traceState?: string;

  /** Human-readable operation name */
  operationName: string;

  /** Timestamp when trace was started (milliseconds since epoch) */
  startTime: number;

  /** Optional parent span ID for nested spans */
  parentSpanId?: string;
}

/**
 * Span attributes for adding context to spans
 */
export interface SpanAttributes {
  /** Service name (e.g., 'frontend-bottlemart') */
  'service.name'?: string;

  /** Cart item count for checkout spans */
  'cart.item_count'?: number;

  /** Shop/store ID for location-specific operations */
  'shop.id'?: string;

  /** Payment amount for payment spans */
  'payment.amount'?: number;

  /** Payment method for payment spans */
  'payment.method'?: string;

  /** User session ID */
  'session.id'?: string;

  /** Device ID */
  'device.id'?: string;

  /** Brand/tenant identifier */
  'tenant.id'?: string;

  /** HTTP method for API request spans */
  'http.method'?: string;

  /** HTTP URL for API request spans */
  'http.url'?: string;

  /** HTTP status code for API response spans */
  'http.status_code'?: number;

  /** Error message if operation failed */
  'error.message'?: string;

  /** Error stack trace if operation failed */
  'error.stack'?: string;

  /** Allow any additional custom attributes */
  [key: string]: string | number | boolean | undefined;
}

/**
 * Telemetry configuration options
 */
export interface TelemetryConfig {
  /** Whether telemetry is enabled */
  enabled: boolean;

  /** Service name for this frontend instance */
  serviceName: string;

  /** Sample rate (0.0 to 1.0, where 1.0 = 100% sampling) */
  sampleRate: number;

  /** Enable debug logging */
  debug: boolean;
}

/**
 * Span status codes
 */
export enum SpanStatusCode {
  /** Operation completed successfully */
  OK = 0,

  /** Operation failed with an error */
  ERROR = 1,

  /** Operation status is unset (default) */
  UNSET = 2,
}

/**
 * Span status
 */
export interface SpanStatus {
  /** Status code */
  code: SpanStatusCode;

  /** Optional status message */
  message?: string;
}

/**
 * Logger callback to break circular dependency between logger and telemetry
 * Telemetry uses this callback to log messages without importing logger directly
 */
export interface LogMetadata {
  timestamp: number;
  level: 'trace' | 'debug' | 'info' | 'warn' | 'error';
  context?: Record<string, unknown>;
}

export type LoggerCallback = (namespace: string, message: string, metadata: LogMetadata) => void;
